import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Updates description HTML when the product variant changes (Horizon variant:update).
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} surface - Region whose innerHTML is replaced with server-built markup
 *
 * @extends {Component<Refs>}
 */
class VariantDescription extends Component {
  requiredRefs = ['surface'];

  /** @type {Record<string, string> | null} */
  #map = null;

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#parseMap();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #parseMap() {
    const el = this.querySelector('script[type="application/json"][data-variant-description-map]');
    if (!el?.textContent) return;
    try {
      this.#map = JSON.parse(el.textContent.trim());
    } catch {
      this.#map = null;
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail.resource;
    if (!variant?.id || !this.#map || !this.refs.surface) return;

    const rawId = variant.id;
    const idStr = String(rawId);
    const gidMatch = typeof rawId === 'string' && rawId.includes('gid://') ? rawId.match(/\d+$/) : null;
    const keys = [idStr, rawId];
    if (gidMatch) keys.push(gidMatch[0]);

    let html;
    for (const key of keys) {
      if (key === undefined || key === null) continue;
      const candidate = this.#map[key];
      if (candidate != null && candidate !== '') {
        html = candidate;
        break;
      }
    }

    if (html != null && html !== '') {
      this.refs.surface.innerHTML = html;
    }
  };
}

if (!customElements.get('variant-description')) {
  customElements.define('variant-description', VariantDescription);
}
